import { FormRow, FormSection } from '@shared/components';
import type { PartnerType } from '@shared/types';
import { PARTNER_TYPE_LABELS } from '@shared/types';
import type { CustomerCreateInput } from '../services/customer.service';

const cls = 'w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500';

const PARTNER_TYPE_ORDER: PartnerType[] = ['customer', 'supplier', 'subcontractor'];

interface Props {
  form: CustomerCreateInput;
  upd: (patch: Partial<CustomerCreateInput>) => void;
  onBlurDuplicate: () => void;
}

export function CustomerFormFieldsBasic({ form, upd, onBlurDuplicate }: Props) {
  const togglePartnerType = (type: PartnerType, checked: boolean) => {
    const current = form.partnerTypes ?? [];
    const next = checked ? [...new Set([...current, type])] : current.filter((t) => t !== type);
    upd({ partnerTypes: next });
  };

  // 仕入先（通販業者を含む）にチェックがある場合は TEL・郵便番号の必須を解除する。
  const isSupplier = form.partnerTypes?.includes('supplier') ?? false;

  return (
    <FormSection title="基本情報">
      <FormRow label="区分" required>
        <div className="flex flex-wrap gap-4 py-1">
          {PARTNER_TYPE_ORDER.map((t) => (
            <label key={t} className="inline-flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.partnerTypes?.includes(t) ?? false}
                onChange={(e) => togglePartnerType(t, e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              {PARTNER_TYPE_LABELS[t]}
            </label>
          ))}
        </div>
      </FormRow>
      <FormRow label="顧客名" required>
        <input
          className={cls}
          value={form.name}
          required
          onChange={(e) => upd({ name: e.target.value })}
          onBlur={onBlurDuplicate}
        />
      </FormRow>
      <FormRow label="フリガナ" required>
        <input
          className={cls}
          value={form.nameKana}
          required
          onChange={(e) => upd({ nameKana: e.target.value })}
          onBlur={onBlurDuplicate}
        />
      </FormRow>
      <FormRow label="ランク" required>
        <div className="space-y-1.5">
          <label className="inline-flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={form.rankMode === 'manual'}
              onChange={(e) => upd({ rankMode: e.target.checked ? 'manual' : 'auto' })}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            手動で固定する（自動算定の対象外）
          </label>
          <select
            className={cls + (form.rankMode === 'auto' ? ' bg-gray-100 text-gray-500' : '')}
            value={form.rank}
            disabled={form.rankMode === 'auto'}
            onChange={(e) => upd({ rank: e.target.value as CustomerCreateInput['rank'] })}
          >
            {(['SSS', 'SS', 'S', 'A', 'B', 'C'] as const).map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          {form.rankMode === 'auto' && (
            <p className="text-xs text-gray-500">取引実績に応じて月次締め時に自動更新されます。</p>
          )}
        </div>
      </FormRow>
      <FormRow label="ステータス" required>
        <select
          className={cls}
          value={form.status}
          onChange={(e) => upd({ status: e.target.value as CustomerCreateInput['status'] })}
        >
          <option value="active">取引中</option>
          <option value="inactive">取引停止</option>
          <option value="prospect">見込み</option>
        </select>
      </FormRow>
      <FormRow label="業種">
        <input
          className={cls}
          value={form.industry}
          onChange={(e) => upd({ industry: e.target.value })}
        />
      </FormRow>
      <FormRow label="TEL" required={!isSupplier}>
        <input
          className={cls}
          value={form.phone}
          required={!isSupplier}
          onChange={(e) => upd({ phone: e.target.value })}
        />
      </FormRow>
      <FormRow label="TEL2">
        <input
          className={cls}
          value={form.tel2 ?? ''}
          onChange={(e) => upd({ tel2: e.target.value || null })}
        />
      </FormRow>
      <FormRow label="FAX">
        <input
          className={cls}
          value={form.fax ?? ''}
          onChange={(e) => upd({ fax: e.target.value || null })}
        />
      </FormRow>
    </FormSection>
  );
}
